import type { ReactNode } from 'react'

import Icon from '@components/utils/Icon'
import { SimpleStatCard, SplitStatCard, BreakdownLine } from '@components/molecules/StatCard'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { rosterCoverage, draftShiftCount, coveragePct } from './rosterCoverage'

import { useEventTimeZone } from '../author_shifts/ShiftAuthoringContext'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('roster_board')

type RosterHeaderProps = {
  eventName: string
  startTime: string
  endTime: string
  shifts: ShiftWithSlots[]
  actions?: ReactNode
}

export function RosterHeader({ eventName, startTime, endTime, shifts, actions }: RosterHeaderProps) {
  const { filled, total } = rosterCoverage(shifts)
  const drafts = draftShiftCount(shifts)
  const openSlots = Math.max(0, total - filled)
  const overallPct = coveragePct(filled, total)
  const dateRange = timeFormatter.dateRange(startTime, endTime, useEventTimeZone())

  return (
    <div className="tw:flex tw:flex-col tw:gap-5 tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:p-6 tw:shadow-xs">
      <div className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-4">
        <div>
          <h2 className="tw:text-lg tw:font-bold tw:text-gray-900">{eventName}</h2>
          <div className="tw:mt-1 tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-gray-500">
            <Icon type="calendar" className="tw:h-4 tw:w-4" aria-hidden />
            {dateRange}
          </div>
        </div>
        {actions && <div className="tw:flex tw:items-center tw:gap-2">{actions}</div>}
      </div>
      <div className="tw:flex tw:flex-wrap tw:gap-3">
        <SplitStatCard value={filled} label={t('stat_spots_filled')}>
          <BreakdownLine icon="check-circle" iconColor="tw:text-success-600" label={t('stat_pct_filled', { pct: overallPct })} />
          <BreakdownLine icon="alert-circle" iconColor="tw:text-warning-600" label={t('stat_open_spots', { count: openSlots })} />
        </SplitStatCard>
        <SimpleStatCard value={drafts} label={t('stat_in_draft')} />
      </div>
    </div>
  )
}
